import {DateTime} from 'luxon';
import {type FC, type ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {selectDetails} from '~/CreateFabricInvitation/reducers/details/selectors';
import {scrollEventTitleModuleIntoView} from '~/CreateFabricInvitation/utils/misc';
import {StartEndController} from '~/common/components/StartEndController/StartEndController';
import {useFormValidations} from '~/common/FabricInvitation/hooks/useFormValidations';
import {validateStartEnd} from '~/common/FabricInvitation/utils/validation';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {SET_GUEST_OPTIONS} from '~/common/fabric/reducers/guestOptions/constants';
import {selectRSVPDeadline} from '~/common/fabric/reducers/guestOptions/selectors';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import './StartEnd.sass';

interface StartEndProps {
  scrollContainerSelector?: string;
  controllerOpen?: boolean;
  onControllerOpenChange?: (open: boolean) => void;
  // biome-ignore lint/style/useNamingConvention: existing error
  TriggerElement?: ReactNode;
  className?: string;
  validator?: (start: string | null, end: string | null) => string | null;
}

export const StartEnd: FC<StartEndProps> = ({
  scrollContainerSelector,
  controllerOpen,
  onControllerOpenChange,
  TriggerElement,
  className,
  validator,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const store = useStore();
  const dispatch = useDispatch();
  const {start, end, known_timezone_name: tzName} = useSelector(selectDetails);
  const rsvpDeadline = useSelector(selectRSVPDeadline);

  const startDate = useMemo(() => {
    if (!start) return undefined;
    if (typeof start === 'object') return DateTime.fromISO(start.toISOString());
    if (typeof start === 'string') return DateTime.fromISO(start);
    return undefined;
  }, [start]);

  const endDate = useMemo(() => {
    if (!end) return undefined;
    if (typeof end === 'object') return DateTime.fromISO(end.toISOString());
    if (typeof end === 'string') return DateTime.fromISO(end);
    return undefined;
  }, [end]);

  // If event start is changed to before rsvp deadline, clear deadline and show toast
  useEffect(() => {
    if (!rsvpDeadline) return;
    const eventStartIsBeforeRsvpDeadline =
      startDate && DateTime.fromISO(rsvpDeadline).diff(startDate).milliseconds > 0;

    if (eventStartIsBeforeRsvpDeadline) {
      dispatch({
        type: SET_GUEST_OPTIONS,
        payload: {
          rsvpDeadline: undefined,
          rsvpDeadlineIsStrict: undefined,
        },
      });

      toastAlert(
        <span className="rsvp-deadline-removed-toast">
          <Typography as="span" variant="paragraph2" semibold>
            RSVP Deadline removed
          </Typography>
          <Typography as="span" variant="paragraph2">
            Deadline was later than the event date, so we removed it.
          </Typography>
        </span>,
        {
          icon: <AlertLineIcon ratio={0.75} />,
          type: 'info',
          position: 'top-right',
        }
      );
    }
  }, [rsvpDeadline, startDate, dispatch]);

  const handleSave = useCallback(
    ({
      start: newStart,
      end: newEnd,
      timezone: newTimezoneName,
    }: {
      start: DateTime | null;
      end: DateTime | null;
      timezone: Timezone | null;
    }) => {
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {
          start: newStart?.toISO(),
          end: newEnd?.toISO(),
          // biome-ignore lint/style/useNamingConvention: existing error
          known_timezone_abbr: newTimezoneName?.timezone_abbr,
          // biome-ignore lint/style/useNamingConvention: existing error
          known_timezone_name: newTimezoneName?.timezone_id,
        },
      });
      scrollEventTitleModuleIntoView();
    },
    [store]
  );

  const validate = useCallback(() => {
    return validateStartEnd(start, end, ref.current, () => onControllerOpenChange?.(true));
  }, [start, end, onControllerOpenChange]);

  useFormValidations('date', validate);

  return (
    <StartEndController
      startDatetime={startDate}
      endDatetime={endDate}
      timezone={tzName}
      scrollContainerSelector={scrollContainerSelector}
      onSave={handleSave}
      open={controllerOpen}
      setOpen={onControllerOpenChange}
      TriggerElement={TriggerElement}
      className={className}
      validator={validator}
      ref={ref}
    />
  );
};
